<template>
  <div>
    <safe-teleport to="#toolbar-portal">
      <q-toolbar
        :class="{ 'q-px-md': $q.screen.gt.xs, 'q-px-lg': $q.screen.gt.md }"
        :style="`border-bottom: 1px solid ${$q.dark.isActive ? 'rgba(255, 255, 255, 0.28)' : 'rgba(0, 0, 0, 0.12)'}`"
        class="q-px-sm row justify-center"
        style="height: 75px"
      >
        <div class="property-compare full-width row items-center full-height">
          <template v-if="$q.screen.gt.xs">
            <div class="q-mr-sm q-my-auto" style="font-size: 20px; font-weight: 500">
              {{ isEditMode ? 'Edit' : 'New' }} Property Report
            </div>
            <q-space />
          </template>
          <q-tabs v-model="step" active-color="primary" class="self-end" dense inline-label no-caps>
            <q-tab
              :label="$q.screen.gt.md ? 'Comp Selection' : ''"
              icon="domain_add"
              name="comp-selection"
            ></q-tab>
            <q-tab
              :disable="
                !isEditMode &&
                reportCopy.comparable_addresses.length === 0 &&
                reportCopy.subject === null
              "
              :label="$q.screen.gt.md ? 'Report Settings' : ''"
              icon="settings"
              name="report-settings"
            ></q-tab>
          </q-tabs>
          <q-space />
          <div
            :style="$q.screen.gt.md ? 'width: 300px' : ''"
            class="q-gutter-x-sm q-my-auto row justify-end"
          >
            <q-btn
              v-if="isEditMode"
              :dense="$q.screen.lt.sm"
              :loading="deleteLoading"
              color="red"
              flat
              no-caps
              outline
              @click="deleteDialog = true"
            >
              <span v-if="$q.screen.gt.sm"> Delete </span>
              <q-icon v-else name="delete" size="medium" />
            </q-btn>
            <q-btn :dense="$q.screen.lt.sm" flat no-caps outline @click="cancel">
              <span v-if="$q.screen.gt.sm"> Cancel </span>
              <q-icon v-else name="close" size="medium" />
            </q-btn>
            <q-btn
              id="intercom-generate-report"
              :dense="$q.screen.lt.sm"
              :disable="
                (step === 'report-settings' && !canSubmit) ||
                (step === 'comp-selection' &&
                  !isEditMode &&
                  reportCopy.comparable_addresses.length === 0 &&
                  reportCopy.subject === null)
              "
              :loading="isSubmitting"
              color="accent"
              no-caps
              unelevated
              @click="handleActionButton"
            >
              <span v-if="$q.screen.gt.sm">
                {{ actionButtonText }}
              </span>
              <q-icon v-else name="save" size="medium" />
              <q-tooltip v-if="!canSubmit && billedPerUnit && step === 'report-settings'">
                Please see Managed Portfolio Settings and mark as included or excluded from your
                portfolio.
              </q-tooltip>
            </q-btn>
          </div>
        </div>
      </q-toolbar>
    </safe-teleport>
    <div v-if="reportCopy" class="property-compare q-gutter-y-lg q-mx-auto">
      <!-- Properties Selection Step -->
      <enter-addresses
        v-show="step === 'comp-selection'"
        :report="reportCopy"
        @update-property-details="updatePropertyDetails"
        @update-report="updateReport"
        @new-custom-property="safelyNavigate"
      />
      <!-- Report Settings Step -->
      <div v-show="step === 'report-settings'" class="q-gutter-y-md q-mx-auto">
        <div :class="$q.screen.lt.lg ? 'q-gutter-y-md' : 'q-gutter-x-md'" class="row">
          <q-card bordered class="col-12 col-lg" flat>
            <q-card-section>
              <div class="text-h6">Basic Settings</div>
              <div>Assign your report a name and any tags you want to include</div>
            </q-card-section>
            <q-separator />
            <q-card-section>
              <div class="row q-gutter-x-sm">
                <q-input
                  v-model="reportCopy.name"
                  class="col-8"
                  clearable
                  dense
                  filled
                  label="Name"
                />
                <q-select
                  v-model="reportCopy.tags"
                  :color="reportCopy.tags.length > 1 ? 'primary' : 'accent'"
                  :options="availableReportTags"
                  class="col"
                  dense
                  multiple
                  options-dense
                  outlined
                  transition-duration="100"
                >
                  <template #prepend>
                    <q-icon name="label" size="14px" />
                  </template>
                  <template #selected>
                    <div class="text-weight-regular text-elipsis" style="font-size: 11px">
                      {{ reportCopy.tags.join(', ') }}
                    </div>
                  </template>
                </q-select>
              </div>
            </q-card-section>
          </q-card>
          <q-card
            v-if="reportCopy.subject && billedPerUnit"
            :class="{
              'bg-amber-1': reportCopy.is_in_portfolio === null,
              'bg-brown-10': reportCopy.is_in_portfolio === null && $q.dark.isActive
            }"
            bordered
            class="col-12 col-lg"
            dense
            flat
            rounded
          >
            <q-card-section class="row items-start">
              <div>
                <div class="text-h6">Managed Portfolio Settings</div>
                <div>
                  This enables you to share the report with clients, owners, or other external
                  parties.
                </div>
              </div>
              <q-space />
              <div v-if="reportCopy.is_in_portfolio === null" class="text-red">Required</div>
            </q-card-section>
            <q-separator />
            <q-card-section>
              <div
                v-if="reportCopy.is_in_portfolio === null"
                :class="$q.screen.lt.lg ? 'column items-center' : 'row items-center'"
              >
                <div>
                  <div>
                    Include
                    <span class="text-bold">
                      {{ reportCopy.subject.building_name ?? reportCopy.subject.street_address }}
                    </span>
                    as part of {{ organization?.display_name }}'s owned / managed portfolio?
                  </div>
                </div>
                <q-space />
                <div :class="{ 'q-mt-sm': $q.screen.lt.lg }" class="q-px-sm">
                  <q-btn class="q-mr-md" no-caps outline @click="setIsInPortfolio(true)">
                    Include
                  </q-btn>
                  <q-btn no-caps outline @click="setIsInPortfolio(false)"> Exclude </q-btn>
                </div>
              </div>
              <div v-else class="column items-center">
                <div
                  v-if="!memberExternalSharing || (isAdmin && adminExternalSharing)"
                  :class="reportCopy.is_in_portfolio ? 'text-green-8' : 'text-red-8'"
                  class="row items-center"
                >
                  <q-icon
                    v-if="reportCopy.is_in_portfolio"
                    class="q-mr-xs"
                    color="green"
                    name="check_circle"
                  ></q-icon>
                  <q-icon v-else class="q-mr-xs" color="red" name="cancel"></q-icon>
                  External sharing {{ reportCopy.is_in_portfolio ? 'enabled. ' : 'disabled. ' }}
                </div>
                <div class="row items-center">
                  <div>
                    This report is {{ reportCopy.is_in_portfolio ? 'included ' : 'excluded ' }}
                    <span class="text-bold">
                      {{ reportCopy.subject.building_name ?? reportCopy.subject.street_address }}
                    </span>
                    {{ reportCopy.is_in_portfolio ? 'in ' : 'from ' }}
                    {{ organization?.display_name }}'s portfolio
                  </div>
                  <q-space />
                  <q-btn
                    color="primary"
                    dense
                    flat
                    no-caps
                    @click="setIsInPortfolio(!reportCopy.is_in_portfolio)"
                  >
                    {{ reportCopy.is_in_portfolio ? 'Exclude?' : 'Include?' }}
                  </q-btn>
                </div>
              </div>
            </q-card-section>
          </q-card>
        </div>
        <div
          :class="$q.screen.lt.lg ? 'q-gutter-y-md q-mt-none' : 'q-gutter-x-md'"
          class="row items-start"
        >
          <email-notifications
            v-model="reportCopy.email_schedule"
            :can-externally-share="
              canExternallyShare(
                billedPerUnit,
                billedPerSeat,
                $auth0.user.value?.internalFeatureFlags,
                reportCopy.is_in_portfolio,
                isAdmin
              )
            "
            :invalid-email-list="invalidEmailList"
            :report-is-in-portfolio="reportCopy.is_in_portfolio"
            class="col-12 col-lg"
          />
          <share-report
            :has-new-owner="hasNewOwner"
            :invite-people-list="invitePeopleList"
            :people-with-access="peopleWithAccess"
            :remove-people-list="removePeopleList"
            :report="reportCopy"
            class="col-12 col-lg"
            @update:invite-people-list="(val) => (invitePeopleList = val)"
            @update:access-level="updateAccessLevel"
            @update:remove-people-list="addToRemovePeopleList"
            @update:report-owner="(val) => (reportCopy.user_id = val)"
            @update:existing-user-role="updateExistingUserRole"
          />
        </div>
        <div v-if="!editMetadata" class="flex justify-end">
          <q-btn label="Edit Metadata" no-caps outline @click="editMetadata = true" />
        </div>
        <edit-metadata
          v-if="editMetadata"
          :metadata="reportCopy.metadata"
          @input="updateMetadata"
        />
      </div>
    </div>
    <q-dialog v-model="deleteDialog" transition-duration="100">
      <q-card style="width: 400px">
        <q-card-section class="row items-center">
          <span class="q-ml-sm">
            Are you sure you want to permanently delete <b>{{ reportCopy.name }}</b
            >?
          </span>
        </q-card-section>
        <q-card-actions align="right">
          <q-btn v-close-popup flat label="Cancel" no-caps unelevated />
          <q-btn
            v-close-popup
            :loading="deleteLoading"
            color="red"
            label="Delete"
            no-caps
            unelevated
            @click="$emit('delete')"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script lang="ts">
import { type PropType, toRaw } from 'vue'
import type { RouteLocation } from 'vue-router'
import {
  type AccessUser,
  FeatureFlag,
  OrganizationVisibility,
  type PropertyReportSubscriptionAttributes
} from 'common'
import EnterAddresses from '@/components/PropertyReports/EnterAddresses.vue'
import EmailNotifications from '@/components/EmailNotifications.vue'
import EditMetadata from '@/components/EditMetadata.vue'
import { useUserStore } from '@/stores/user'
import {
  canExternallyShare,
  computePropertyEdits,
  deconstructPropertyEdits,
  isInternalEmail
} from '@/utils'
import { useStripeStore } from '@/stores/stripe.ts'
import ShareReport from '@/components/ShareReport.vue'

export default {
  name: 'PropertyReportSettings',
  components: {
    ShareReport,
    EmailNotifications,
    EnterAddresses,
    EditMetadata
  },
  props: {
    report: {
      type: Object as PropType<PropertyReportSubscriptionAttributes>,
      required: true
    },
    initialStep: {
      required: false,
      type: String as PropType<'comp-selection' | 'report-settings'>,
      default: 'comp-selection'
    }
  },
  emits: ['submit', 'delete', 'update-property-details', 'cancel'],
  data() {
    return {
      reportCopy: this.report,
      editMetadata: false,
      deleteDialog: false,
      deleteLoading: false,
      isSubmitting: false,
      manualPropertyEdits: {} as Record<string, Record<string, string | number>>,
      step: 'comp-selection' as 'comp-selection' | 'report-settings',
      peopleWithAccess: [] as AccessUser[],
      removePeopleList: [] as AccessUser[],
      invitePeopleList: [] as AccessUser[],
      updatedAccessUsers: [] as AccessUser[]
    }
  },
  computed: {
    isEditMode() {
      return this.report.id !== -1
    },
    availableReportTags(): string[] {
      return useUserStore().availableReportTags
    },
    canSubmit() {
      if (this.billedPerUnit) {
        // if there is a subject, require portfolio decision (included or excluded)
        if (this.reportCopy.subject) {
          return this.reportCopy.is_in_portfolio !== null
        }
        // if they don't have a subject, just check for comps
        return this.reportCopy.comparable_addresses.length > 0
      }
      return this.reportCopy.comparable_addresses.length > 0 || this.reportCopy.subject !== null
    },
    propertyEdits() {
      return computePropertyEdits(this.manualPropertyEdits)
    },
    billedPerUnit() {
      return useStripeStore().billedPerUnit
    },
    billedPerSeat() {
      return useStripeStore().billedPerSeat
    },
    isAdmin() {
      return useUserStore().isAdmin
    },
    organization() {
      return useUserStore().organization
    },
    invalidEmailList() {
      if (!this.reportCopy.emails) return false
      return this.reportCopy.emails.some((email) => {
        if (
          canExternallyShare(
            this.billedPerUnit,
            this.billedPerSeat,
            this.$auth0.user.value?.internalFeatureFlags,
            this.reportCopy.is_in_portfolio,
            this.isAdmin
          )
        ) {
          return false
        }

        return !isInternalEmail(email, useUserStore().organizationMembers)
      })
    },
    actionButtonText() {
      if (this.isEditMode) {
        if (this.canSubmit || this.step === 'report-settings') {
          return 'Save Report'
        }
        return 'Next: Report Settings'
      }
      if (this.step === 'comp-selection') {
        return 'Next: Report Settings'
      }
      return 'Generate Report'
    },
    organizationMembers() {
      return useUserStore().organizationMembers
    },
    hasNewOwner() {
      return this.reportCopy.user_id !== this.report.user_id
    },
    memberExternalSharing() {
      return (
        this.$auth0.user.value?.internalFeatureFlags.includes(
          FeatureFlag.MEMBER_EXTERNAL_SHARING
        ) ?? false
      )
    },
    adminExternalSharing() {
      return (
        this.$auth0.user.value?.internalFeatureFlags.includes(FeatureFlag.ADMIN_EXTERNAL_SHARING) ??
        false
      )
    }
  },
  watch: {
    step(newStep, oldStep) {
      if (oldStep && oldStep !== newStep) {
        this.$tracker.changedTab(oldStep, newStep, 'property_report_settings')
      }
    },
    'reportCopy.subject'(newSubject, oldSubject) {
      if (oldSubject === null && newSubject !== null) {
        this.reportCopy.name = newSubject.building_name || newSubject.street_address || ''
      }
    },
    invitePeopleList: {
      handler(newList) {
        // Check each user in the invite list and set read_only if they are external
        newList.forEach((user: AccessUser) => {
          if (!isInternalEmail(user.email, this.organizationMembers)) {
            if (!user.read_only) {
              user.read_only = true
            }
          }
        })
      },
      deep: true
    },
    organizationMembers: {
      handler(newMembers: AccessUser[]) {
        if (newMembers.length > 0) {
          // Always ensure the report owner is in peopleWithAccess
          const owner = newMembers.find((_) => _.id === this.reportCopy.user_id)
          if (owner) {
            const ownerAlreadyInAccess = this.peopleWithAccess.some((user) => user.id === owner.id)
            if (!ownerAlreadyInAccess) {
              this.peopleWithAccess.push(owner)
            }
          }

          // For edit mode, also add existing access users
          if (this.isEditMode && this.reportCopy.access_users) {
            this.reportCopy.access_users.forEach((accessUser) => {
              const alreadyInAccess = this.peopleWithAccess.some(
                (u) => u.id === accessUser.id || u.email === accessUser.email
              )
              if (!alreadyInAccess) {
                this.peopleWithAccess.push(accessUser)
              }
            })
          }
        }
      },
      immediate: true
    }
  },
  created() {
    // impossible to copy a reactive object, so we need to clone it
    this.reportCopy = structuredClone(toRaw(this.report))

    if (Object.keys(this.report.metadata || {}).length > 0) {
      this.editMetadata = true
    }

    this.step = this.initialStep
  },
  methods: {
    canExternallyShare,
    emailCanBeExternallyShared(email: string) {
      if (
        canExternallyShare(
          this.billedPerUnit,
          this.billedPerSeat,
          this.$auth0.user.value?.internalFeatureFlags,
          this.reportCopy.is_in_portfolio,
          this.isAdmin
        )
      ) {
        return true
      }

      return isInternalEmail(email, this.organizationMembers)
    },
    cancel() {
      // we call the cancel event instead of using a router-link so that
      // the parent component can handle the navigation and display a
      // dialog to confirm the user wants to leave the page if needed
      if (this.isEditMode) {
        this.$emit('cancel', { name: 'Property Report', params: { reportId: this.report.id } })
      } else {
        this.$emit('cancel', { name: 'Dashboard' })
      }
    },
    safelyNavigate(to: RouteLocation) {
      this.$emit('cancel', to)
    },
    updateReport(report: PropertyReportSubscriptionAttributes) {
      this.reportCopy = report
    },
    updateMetadata(metadata: Record<string, string>) {
      this.reportCopy.metadata = metadata
    },
    updatePropertyDetails(propertyId: string, key: string, value: string | number) {
      if (!this.manualPropertyEdits[propertyId]) {
        this.manualPropertyEdits[propertyId] = {}
      }
      this.manualPropertyEdits[propertyId][key] = value
      this.$emit('update-property-details', deconstructPropertyEdits(this.propertyEdits))
    },
    handleActionButton() {
      if (this.isEditMode) {
        if (this.canSubmit) {
          this.submit()
          return
        }
      }
      if (this.step === 'comp-selection') {
        this.step = 'report-settings'
        return
      } else {
        if (this.canSubmit) {
          this.submit()
          return
        }
      }
    },
    submit() {
      const noReportName = !this.reportCopy.name || this.reportCopy.name.trim() === ''
      // Validate report name
      if (this.isEditMode && noReportName) {
        this.$q.notify({
          type: 'negative',
          message: 'Please enter a report name',
          position: 'top',
          timeout: 3000
        })
        return
      }
      if (noReportName) {
        // we initialize the report name with the subject building name or street address
        if (this.reportCopy.subject) {
          this.reportCopy.name =
            this.reportCopy.subject.building_name || this.reportCopy.subject.street_address
        } else {
          this.reportCopy.name =
            [...new Set(this.reportCopy.comparable_addresses.map((_) => _.city))]
              .sort()
              .join('-')
              .split(' ')
              .join('-') + ' Property Report'
        }
      }

      this.reportCopy.property_edits = this.propertyEdits
      this.isSubmitting = true
      if (this.isEditMode) {
        this.$emit(
          'submit',
          this.reportCopy,
          this.invitePeopleList,
          this.removePeopleList,
          this.updatedAccessUsers,
          this.report.user_id
        )
      } else {
        this.$emit('submit', this.reportCopy, this.invitePeopleList)
      }
    },
    setIsInPortfolio(value: boolean | null) {
      this.reportCopy.is_in_portfolio = value
    },
    addToRemovePeopleList(value: AccessUser) {
      this.removePeopleList.push(value)
    },
    updateAccessLevel(
      organizationVisibility: OrganizationVisibility | null,
      isPublic: string | null
    ) {
      this.reportCopy.public_id = isPublic
      this.reportCopy.organization_visibility = organizationVisibility
    },
    updateExistingUserRole(updatedUser: AccessUser) {
      const userIndex = this.peopleWithAccess.findIndex(
        (u) => u.id === updatedUser.id || u.email === updatedUser.email
      )
      if (userIndex !== -1) {
        this.peopleWithAccess[userIndex] = updatedUser

        // Track this user as having updated access if they were in the original report
        if (this.isEditMode) {
          const originalUser = this.report.access_users?.find(
            (u) => u.id === updatedUser.id || u.email === updatedUser.email
          )
          const existingIndex = this.updatedAccessUsers.findIndex(
            (u) => u.id === updatedUser.id || u.email === updatedUser.email
          )

          // Special case: if this is the old owner (due to ownership transfer), always track them
          const isOldOwner = this.hasNewOwner && updatedUser.id === this.report.user_id

          if ((originalUser && originalUser.read_only !== updatedUser.read_only) || isOldOwner) {
            // User's access has changed from original - add or update in tracking array
            if (existingIndex !== -1) {
              this.updatedAccessUsers[existingIndex] = updatedUser
            } else {
              this.updatedAccessUsers.push(updatedUser)
            }
          } else if (existingIndex !== -1 && !isOldOwner) {
            // User's access matches original - remove from tracking array
            this.updatedAccessUsers.splice(existingIndex, 1)
          }
        }
      }
    }
  }
}
</script>
<style scoped>
.text-elipsis {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.property-compare {
  max-width: 1600px;
}
</style>
